// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)

import React, { createContext, useCallback, useContext, useReducer } from "react";
import type { PropsWithChildren } from "react";
import type { HeatmapData, PlotProps } from ".";

interface DataState {
	plotData: PlotProps[] | null;
	trialPolygon: GeoJSON.Polygon | null;
	heatmapData: HeatmapData[] | null;
	heatmapDataAllDates: HeatmapData[] | null;
	dataLayerNames: string[];
	hideExclusions: boolean;
}

interface DataFunctions {
	updatePlotData: (data: PlotProps[] | null) => void;
	updateHeatMapData: (data: HeatmapData[] | null) => void;
	updateHeatMapDataAllDates: (data: HeatmapData[] | null) => void;
	updateTrialPolygon: (polygon: GeoJSON.Polygon) => void;
	updateDataLayerNames: (names: string[]) => void;
	updateHideExclusions: (toggle: boolean) => void;
}

type DataAction =
	| {
			type: "plots";
			data: PlotProps[] | null;
	  }
	| { type: "heatmap"; data: HeatmapData[] | null }
	| { type: "heatmap all dates"; data: HeatmapData[] | null }
	| { type: "trial"; polygon: GeoJSON.Polygon }
	| { type: "data layer"; names: string[] }
	| { type: "exclusions"; toggle: boolean };

const dataReducer = (state: DataState, action: DataAction) => {
	switch (action.type) {
		case "plots":
			return { ...state, plotData: action.data };
		case "heatmap":
			return { ...state, heatmapData: action.data };
		case "heatmap all dates":
			return { ...state, heatmapDataAllDates: action.data };
		case "trial":
			return { ...state, trialPolygon: action.polygon };
		case "data layer":
			return { ...state, dataLayerNames: action.names };
		case "exclusions":
			return { ...state, hideExclusions: action.toggle };
		default:
			return state;
	}
};

const DataContext = createContext<DataState | null>(null);
const DataDispatchContext = createContext<DataFunctions | null>(null);

const DataProvider = ({ children }: PropsWithChildren) => {
	const [data, dispatch] = useReducer(dataReducer, {
		plotData: null,
		heatmapData: null,
		heatmapDataAllDates: null,
		trialPolygon: null,
		dataLayerNames: [],
		hideExclusions: true,
	});

	const updatePlotData = useCallback((data: PlotProps[] | null) => dispatch({ type: "plots", data }), []);

	const updateHeatMapData = useCallback((data: HeatmapData[] | null) => dispatch({ type: "heatmap", data }), []);

	const updateHeatMapDataAllDates = useCallback(
		(data: HeatmapData[] | null) => dispatch({ type: "heatmap all dates", data }),
		[],
	);

	const updateTrialPolygon = useCallback((polygon: GeoJSON.Polygon) => dispatch({ type: "trial", polygon }), []);

	const updateDataLayerNames = useCallback((names: string[]) => dispatch({ type: "data layer", names }), []);

	const updateHideExclusions = useCallback((toggle: boolean) => dispatch({ type: "exclusions", toggle }), []);

	return (
		<DataContext.Provider value={data}>
			<DataDispatchContext.Provider
				value={{
					updatePlotData,
					updateHeatMapData,
					updateHeatMapDataAllDates,
					updateTrialPolygon,
					updateDataLayerNames,
					updateHideExclusions,
				}}
			>
				{children}
			</DataDispatchContext.Provider>
		</DataContext.Provider>
	);
};

const useData = () => {
	const context = useContext(DataContext);

	if (context === null) {
		throw new Error("DataContext is not provided");
	}

	return context;
};

const useDataDispatch = () => {
	const context = useContext(DataDispatchContext);

	if (context === null) {
		throw new Error("DataDispatchContext is not provided");
	}

	return context;
};

export { DataProvider, useData, useDataDispatch };
